import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FilterChipDto,
  GetLagerTransakcijeTableQuery,
  IGetLagerTransakcijeTableQuery,
  LagerTransakcijeClient,
  LagerTransakcijeTableDto,
} from '@kodit/core/data-api';
import { LocationService } from '@kodit/core/services';
import { toCamelCase } from '@kodit/core/shared';
import { LoaderService } from 'libs/core/shared-ui/src/lib/loader/loader.service';
import {
  ActionClass,
  ActionType,
  OnTableInit,
  TableConfig,
} from 'libs/core/shared-ui/src/lib/table/table-common';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LagerTransakcijeFilterComponent } from '../lager-transakcije-filter/lager-transakcije-filter.component';
import { LagerTransakcijeFilterService } from '../lager-transakcije-filter/lager-transakcije-filter.service';
import { DocumentPreviewService } from '../../../../../services/src/lib/document-preview.service';

@Component({
  selector: 'kodit-lager-transakcije-table',
  templateUrl: './lager-transakcije-table.component.html',
  styleUrls: ['./lager-transakcije-table.component.scss'],
})
export class LagerTransakcijeTableComponent
  implements OnInit, OnDestroy, OnTableInit {
  /** Subs */
  private _subs: Subscription = new Subscription();

  /** Confings */
  tableConfig: TableConfig;

  /** Props */
  isLoading = true;
  lagerTransakcijeTable: LagerTransakcijeTableDto[] = [];
  chipItems: FilterChipDto[] = [];
  predmetStavkeId: number;
  lagerId: number;

  @Input() showCardBlock: boolean = true;

  constructor(
    private _loaderService: LoaderService,
    private _client: LagerTransakcijeClient,
    private _route: ActivatedRoute,
    private _locationService: LocationService,
    private _filterService: LagerTransakcijeFilterService,
    private _docPreviewService: DocumentPreviewService
  ) {}

  ngOnInit(): void {
    // get router data
    this._route.params.subscribe((data) => {
      if (data['id']) {
        this.predmetStavkeId = Number.parseInt(data['id']);
      }
    });
    this._load(new GetLagerTransakcijeTableQuery());
    this.setTableConfig().then();

    this._subs.add(
      this._filterService.getAdvancedFilterSubmitted.subscribe(
        (filterResult) => {
          if (filterResult?.shouldApplyFilter) {
            this.tableConfig.advancedFilter.data = filterResult.filterData;
            this._load(this.tableConfig.advancedFilter.data);
          }
        }
      )
    );
  }

  // nazivGrupe
  async setTableConfig(): Promise<void> {
    this.tableConfig = new TableConfig({
      tableHeader: 'Transakcije robe na lageru',
      rowsPerPage: 10,
      onChipRemove: (chips: FilterChipDto) => this._handleCihpRemove(chips),
      columns: [
        // {
        //   field: 'nazivGrupe',
        //   header: 'Magacin',
        //   type: 'text',
        //   //shouldDisplayByCondition: () => this._displayNazivMagacina(),
        // },
        {
          field: 'tipDto',
          subField: 'tipStr',
          header: 'Tip transakcije',
          type: 'badge',
          styleClass: 'tip-transakcije',
          styleClassField: 'tipBadgeStr',
        },
        {
          header: 'Račun',
          field: 'rutiranjeVezanogRacuna',
          subField: 'label',
          type: 'link',
          linkCallbackFunction: (itemIndex: number) => this._onInfo(itemIndex),
        },
        {
          field: 'rutiranjeStranke',
          subField: 'naziv',
          header: 'Stranka',
          type: 'link',
          linkCallbackFunction: (itemIndex: number) =>
            this._goToStranka(itemIndex),
        },
        {
          field: 'promena',
          header: 'Promena stanja',
          type: 'number',
        },
        {
          field: 'prodajnaCena',
          header: 'Prodajna cena i vrednost',
          type: 'currency',
          currencyAlphaCharField: 'valutaText',
          columns: [{ field: 'prodajnaVrednost', type: 'currency' }],
        },
        {
          field: 'nabavnaCena',
          header: 'Nabavna cena i vrednost',
          type: 'currency',
          currencyAlphaCharField: 'valutaText',
          columns: [{ field: 'nabavnaVrednost', type: 'currency' }],
        },
      ],
      headerActions: [
        {
          type: ActionType.CUSTOM,
          actionClass: ActionClass.OUTLINED,
          icon: 'fa-regular fa-print',
          isVisible: true,

          callback: () => {
            // ToDo: add filters

            this._docPreviewService.openDialog(
              `lagertransakcije/get-lagertransakcije-pdf/${encodeURIComponent(
                'Prikaz lager transakcija'
              )}`,
              {
                predmetStavkeId: this.predmetStavkeId.toString(),
              }
            );
          },
        },
      ],
      advancedFilter: {
        component: LagerTransakcijeFilterComponent,
        data: new GetLagerTransakcijeTableQuery(),
      },
    });
  }

  private _handleCihpRemove(removedChip: FilterChipDto) {
    this.tableConfig.advancedFilter.data[toCamelCase(removedChip.key)] =
      removedChip.defaultValue;
    this._load(this.tableConfig.advancedFilter.data);
  }

  private _goToStranka(itemIndex: number) {
    const raucn = this.lagerTransakcijeTable[itemIndex];
    this._locationService.routeToStrankaKartica(
      raucn.rutiranjeStranke.strankaId,
      raucn.rutiranjeStranke.tipStranke
    );
  }

  private _onInfo(itemIndex: number): void {
    const vezaniRacun = this.lagerTransakcijeTable[itemIndex]
      .rutiranjeVezanogRacuna;
    this._locationService.routeToRacunKartica(
      vezaniRacun.racunId,
      vezaniRacun.tip,
      vezaniRacun.vrsta
    );
  }

  load() {
    this._load(this.tableConfig?.advancedFilter?.data ?? new GetLagerTransakcijeTableQuery());
  }

  private _load(request?: IGetLagerTransakcijeTableQuery) {
    request.predmetStavkeId = this.predmetStavkeId;
    this.isLoading = true;
    this._subs.add(
      this._client
        .getForTable(request as GetLagerTransakcijeTableQuery)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe((res) => {
          this.lagerTransakcijeTable = res.data.lagerTransakcijeTableDtoList;
          this.chipItems = res.data.activeFilters;
        })
    );
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }
}
